// Maps the two order formats the venue Orders page reads into one display shape.
//
//  legacy      → `Orders` (capital O): web QR + legacy mobile orders, keyed by bizId.
//                Prices are dollars. Status lives in `OrderStatus`; `orderStatus`
//                is the service mode (e.g. PENDING_ON_APPROACH).
//  integrated  → `orders` (lowercase): POS-integrated orders (Square etc.) written
//                by newFunctions, keyed by businessId = Business doc ID.
//                Prices are cents. Status is owned by the POS / Cloud Functions,
//                so these are read-only in the web app.
//
// Nothing here writes or copies orders; it only reshapes what was read.

export const ORDER_SOURCE = { LEGACY: "legacy", INTEGRATED: "integrated" };

// Statuses that are not shown on the Orders page at all.
const HIDDEN_STATUSES = new Set(["cancelled", "refunded", "rejected", "draft"]);

const STATUS_LABELS = {
  new: "New",
  waiting: "New",
  pending: "Sent to POS",
  submitted: "Sent to POS",
  accepted: "Accepted",
  preparing: "Preparing",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Cancelled",
  refunded: "Refunded",
  rejected: "Rejected by POS",
  payment_issue: "Payment issue",
};

const POS_NAMES = { square: "Square", doshii: "Doshii", lightspeed: "Lightspeed" };

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const roundCents = (n) => Math.round(n * 100) / 100;

/** Firestore Timestamp | Date | ISO string | millis → Date (or null). */
export const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === "function") return v.toDate();
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6));
  if (typeof v._seconds === "number") return new Date(v._seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const sumOrNull = (prices) =>
  prices.length > 0 && prices.every((p) => p !== null)
    ? roundCents(prices.reduce((a, b) => a + b, 0))
    : null;

// ── Legacy `Orders` ─────────────────────────────────────────────────────────

const legacyOptionNames = (dish) => {
  const names = [];
  if (dish.selectedSize?.size) names.push(dish.selectedSize.size);
  (dish.selectedOptions || []).forEach((o) => {
    if (typeof o === "string") names.push(o);
    else if (o?.name) names.push(o.name);
  });
  (dish.dishOptions || []).forEach((opt) => {
    if (typeof opt === "string") {
      names.push(opt);
    } else if (opt && typeof opt === "object") {
      const chosen = (opt.optionItems || []).filter((i) => i.enabled).map((i) => i.name);
      if (chosen.length) names.push(...chosen);
      else if (opt.name && !opt.optionItems) names.push(opt.name);
    }
  });
  return names.filter(Boolean);
};

const legacyLinePrice = (dish, quantity) => {
  const line = toNumber(dish.lineTotal);
  if (line !== null) return roundCents(line);
  const unit =
    toNumber(dish.dishTotalPrice) ??
    toNumber(dish.selectedSize?.price) ??
    toNumber(dish.dishAvailSize?.[0]?.price) ??
    toNumber(dish.dishPrice1);
  return unit === null ? null : roundCents(unit * quantity);
};

const legacyServiceLabel = (raw) => {
  const mode = `${raw.orderStatus || ""} ${raw.orderPaymentType || ""}`.toLowerCase();
  return mode.includes("approach") ? "Submit Upon Approach" : "Submit Once Seated";
};

export function fromLegacyOrder(raw) {
  const items = (raw.dishes || []).map((dish) => {
    const quantity = toNumber(dish.dishCount ?? dish.quantity) || 1;
    return {
      name: dish.dishName || "Item",
      quantity,
      options: legacyOptionNames(dish),
      price: legacyLinePrice(dish, quantity),
      alcoholic: dish.dishAlc === true,
    };
  });
  const status = String(raw.OrderStatus || "new").toLowerCase();
  return {
    key: `${ORDER_SOURCE.LEGACY}:${raw.id}`,
    id: raw.id,
    source: ORDER_SOURCE.LEGACY,
    readOnly: false,
    raw,
    orderNumber: raw.orderNumber || null,
    items,
    total: sumOrNull(items.map((i) => i.price)),
    status,
    statusLabel: STATUS_LABELS[status] || raw.OrderStatus || "New",
    table: raw.orderTableNo != null && raw.orderTableNo !== "" ? String(raw.orderTableNo) : null,
    customerName: raw.userFirstName || null,
    customerUserId: raw.userId || null,
    serviceLabel: legacyServiceLabel(raw),
    accessibilityNotes: raw.accessibilityNotes || null,
    time: toDate(raw.timestamp) || toDate(raw.date),
    posName: null,
    ageVerification: raw.ageVerification || null,
  };
}

// ── Integrated `orders` (amounts in cents) ──────────────────────────────────

const centsToDollars = (v) => {
  const n = toNumber(v);
  return n === null ? null : roundCents(n / 100);
};

const integratedOptionNames = (item) => {
  const names = [];
  if (item.selectedVariant?.name) names.push(item.selectedVariant.name);
  (item.selectedOptions || []).forEach((c) => {
    if (c?.name) names.push(c.quantity > 1 ? `${c.name} ×${c.quantity}` : c.name);
  });
  // Shape sent by the mobile app: options[{ name, choices[{ name, quantity }] }]
  (item.options || []).forEach((group) => {
    (group?.choices || []).forEach((c) => {
      if (c?.name) names.push(c.quantity > 1 ? `${c.name} ×${c.quantity}` : c.name);
    });
  });
  return names;
};

const SERVICE_BY_TYPE = {
  pickup: "Submit Upon Approach",
  dine_in: "Submit Once Seated",
  delivery: "Delivery",
};

export function fromIntegratedOrder(raw) {
  const items = (raw.items || []).map((item) => {
    const quantity = toNumber(item.quantity) || 1;
    const line =
      toNumber(item.totalPrice) ??
      (toNumber(item.unitPrice) === null ? null : toNumber(item.unitPrice) * quantity);
    return {
      name: item.name || "Item",
      quantity,
      options: integratedOptionNames(item),
      price: line === null ? null : roundCents(line / 100),
      alcoholic: item.dishAlc === true || item.isAlcoholic === true,
    };
  });
  const status = String(raw.status || "pending").toLowerCase();
  const total =
    centsToDollars(raw.total) ?? centsToDollars(raw.subtotal) ?? sumOrNull(items.map((i) => i.price));
  return {
    key: `${ORDER_SOURCE.INTEGRATED}:${raw.id}`,
    id: raw.id,
    source: ORDER_SOURCE.INTEGRATED,
    readOnly: true,
    raw,
    orderNumber: raw.orderNumber || null,
    items,
    total,
    status,
    statusLabel: STATUS_LABELS[status] || raw.status || "Sent to POS",
    table: raw.tableNumber != null && raw.tableNumber !== "" ? String(raw.tableNumber) : null,
    customerName: raw.customer?.name || null,
    customerUserId: raw.customer?.userId || null,
    serviceLabel: SERVICE_BY_TYPE[raw.type] || null,
    accessibilityNotes: raw.accessibilityNotes || null,
    time: toDate(raw.createdAt) || toDate(raw.requestedAt),
    posName: POS_NAMES[raw.source] || (raw.source ? String(raw.source) : "POS"),
    ageVerification: raw.ageVerification || null,
  };
}

/**
 * Merge both formats into one list, oldest first (the page's existing order,
 * so the next order to make is at the top). Orders without a time go last.
 */
export function mergeOrders(legacyOrders, integratedOrders) {
  const merged = [
    ...(legacyOrders || []).filter(Boolean).map(fromLegacyOrder),
    ...(integratedOrders || []).filter(Boolean).map(fromIntegratedOrder),
  ];
  return merged.sort((a, b) => {
    const ta = a.time ? a.time.getTime() : Infinity;
    const tb = b.time ? b.time.getTime() : Infinity;
    if (ta !== tb) return ta - tb;
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

export const isHiddenOrder = (o) => HIDDEN_STATUSES.has(o.status);
export const isCompletedOrder = (o) => o.status === "completed";
export const isActiveOrder = (o) => !isHiddenOrder(o) && !isCompletedOrder(o);

export const formatMoney = (n) => (n === null || n === undefined ? "—" : `$${n.toFixed(2)}`);

export const formatOrderTime = (d) =>
  d
    ? d.toLocaleString("en-AU", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
